/* c-hannah-suggests — "A note from Hannah" PDP popup

   Every time the note opens it shows the next "pass": another pair of suggested products,
   the order flipped, and a Hannah message about one of the products on screen.
     3 suggestions → AB, BC, CA, BA, CB, AC      2 suggestions → AB, BA

   Desktop: opens by itself after a delay, stays open a few seconds if nobody touches it, then
            re-opens on a timer, up to a maximum per page.
   Mobile:  opens by itself once per visit, only after the shopper has scrolled past the main
            add-to-cart button. After that, the pill opens it on tap.
   Both stop opening by themselves once the shopper closes it or adds something to the cart. */
(function () {
  const STOPPED_KEY = 'c-hannah-auto-stopped';
  const MOBILE_OPENED_KEY = 'c-hannah-mobile-opened';
  const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');

  function storageGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function storageSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* storage blocked */ }
  }

  function reveal(el) {
    el.hidden = false;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add('is-visible'); });
    });
  }

  function conceal(el) {
    el.classList.remove('is-visible');
    el.hidden = true;
  }

  // Hannah's lines read as quotes, like the design: “…”
  function quoted(text) {
    const trimmed = text.trim();
    return /^[“"]/.test(trimmed) ? trimmed : '“' + trimmed + '”';
  }

  // Windows of `size` products starting at each position, then the same windows flipped
  function buildPasses(count, size) {
    const visible = Math.max(1, Math.min(size, count));
    const forward = [];
    for (let start = 0; start < count; start++) {
      const pass = [];
      for (let offset = 0; offset < visible; offset++) pass.push((start + offset) % count);
      forward.push(pass);
    }
    const seen = {};
    return forward.concat(forward.map(function (pass) { return pass.slice().reverse(); }))
      .filter(function (pass) {
        const key = pass.join(',');
        if (seen[key]) return false;
        seen[key] = true;
        return true;
      });
  }

  function initCard(card, onAdded) {
    const dataEl = card.querySelector('[data-c-hannah-variants]');
    const addBtn = card.querySelector('[data-c-hannah-add]');
    const priceEl = card.querySelector('[data-c-hannah-price]');
    const compareEl = card.querySelector('[data-c-hannah-compare]');
    const errorEl = card.querySelector('[data-c-hannah-error]');
    const selects = Array.prototype.slice.call(card.querySelectorAll('[data-c-hannah-option]'));
    if (!dataEl || !addBtn) return;

    const variants = JSON.parse(dataEl.textContent);

    function currentVariant() {
      if (!selects.length) return variants[0];
      return variants.find(function (variant) {
        return selects.every(function (select) {
          return variant.options[Number(select.dataset.cHannahOption)] === select.value;
        });
      });
    }

    function setError(message) {
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    function update() {
      const variant = currentVariant();
      const buyable = Boolean(variant && variant.available);

      addBtn.disabled = !buyable;
      addBtn.title = buyable ? '' : (variant ? 'Sold out' : 'Unavailable');
      if (variant) {
        priceEl.textContent = variant.price;
        if (compareEl) {
          compareEl.textContent = variant.compare || '';
          compareEl.hidden = !variant.compare;
        }
      }
      setError('');
    }

    function add() {
      const variant = currentVariant();
      if (!variant || !variant.available) return;

      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(function (section) { return section.id; })
        : [];

      addBtn.setAttribute('aria-busy', 'true');
      setError('');

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
          sections: sections,
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok || body.status) {
              throw new Error(body.description || body.message || 'Could not add to bag. Please try again.');
            }
            return body;
          });
        })
        .then(function (body) {
          onAdded();
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(body);
          } else {
            window.location = window.Shopify.routes.root + 'cart';
          }
        })
        .catch(function (error) {
          setError(error.message);
        })
        .finally(function () {
          addBtn.removeAttribute('aria-busy');
        });
    }

    selects.forEach(function (select) { select.addEventListener('change', update); });
    addBtn.addEventListener('click', add);
    update();
  }

  function init(root) {
    if (root.dataset.cHannahReady) return;
    root.dataset.cHannahReady = 'true';

    const pill = root.querySelector('[data-c-hannah-open]');
    const dialog = root.querySelector('[data-c-hannah-dialog]');
    const closeBtn = root.querySelector('[data-c-hannah-close]');
    const productsEl = root.querySelector('.c-hannah__products');
    const bodyEl = root.querySelector('[data-c-hannah-body]');
    const quoteEl = root.querySelector('[data-c-hannah-quote]');
    const cards = Array.prototype.slice.call(root.querySelectorAll('[data-c-hannah-card]'));

    const seconds = function (name, fallback) {
      const value = Number(root.dataset[name]);
      return (isNaN(value) ? fallback : value) * 1000;
    };
    const pillDelay = seconds('delay', 5);
    const autoOpenAfter = seconds('autoOpen', 10);
    const visibleFor = seconds('visibleFor', 10);
    const reopenEvery = seconds('reopenEvery', 30);
    const maxAutoOpens = Number(root.dataset.maxAutoOpens) || 3;
    const mobileAutoOpen = root.dataset.mobileAutoOpen === 'true';
    const useProductMessages = root.dataset.quoteSource === 'product';
    const fixedQuote = root.dataset.fallbackQuote || (quoteEl ? quoteEl.textContent : '');

    const passes = buildPasses(cards.length, Number(root.dataset.maxProducts) || 2);
    let passIndex = -1;
    let lastMessage = '';
    let autoOpens = 0;
    let autoTimer = null;
    let hideTimer = null;
    let hovering = false;

    /* ── Passes ── */
    function showNextPass() {
      passIndex = (passIndex + 1) % passes.length;
      const pass = passes[passIndex];

      cards.forEach(function (card) { card.hidden = true; });
      pass.forEach(function (cardIndex) {
        productsEl.appendChild(cards[cardIndex]);
        cards[cardIndex].hidden = false;
      });
      productsEl.scrollLeft = 0;
      if (bodyEl) bodyEl.scrollTop = 0;

      if (!quoteEl) return;
      if (!useProductMessages) {
        quoteEl.textContent = fixedQuote;
        return;
      }
      const messages = pass
        .map(function (cardIndex) { return cards[cardIndex].dataset.message; })
        .filter(Boolean);
      const fresh = messages.filter(function (message) { return message !== lastMessage; });
      const pool = fresh.length ? fresh : messages;
      lastMessage = pool.length ? pool[Math.floor(Math.random() * pool.length)] : '';
      quoteEl.textContent = lastMessage ? quoted(lastMessage) : fixedQuote;
    }

    /* ── Open / close ── */
    function isOpen() {
      return !dialog.hidden;
    }

    function open(byUser) {
      clearTimeout(hideTimer);
      showNextPass();
      conceal(pill);
      pill.setAttribute('aria-expanded', 'true');
      reveal(dialog);
      if (byUser) closeBtn.focus({ preventScroll: true });
    }

    function close() {
      clearTimeout(hideTimer);
      conceal(dialog);
      pill.setAttribute('aria-expanded', 'false');
      reveal(pill);
    }

    function stopAutoOpen() {
      storageSet(STOPPED_KEY, '1');
      clearTimeout(autoTimer);
      clearTimeout(hideTimer);
    }

    function autoStopped() {
      return storageGet(STOPPED_KEY) === '1';
    }

    /* ── Automatic opening ── */
    function cartDrawerOpen() {
      const drawer = document.querySelector('cart-drawer');
      return Boolean(drawer && drawer.classList.contains('active'));
    }

    // Klaviyo sign-up forms and other modals: never stack the note on top of them. The PDP keeps
    // several closed dialogs in the DOM (cart drawer, size guide, Swym), hidden with visibility or
    // opacity, so only a dialog that is really painted on screen counts.
    function otherModalOpen() {
      return Array.prototype.some.call(
        document.querySelectorAll('[role="dialog"][aria-modal="true"]'),
        function (el) {
          if (root.contains(el)) return false;
          const rect = el.getBoundingClientRect();
          if (rect.width < 1 || rect.height < 1) return false;
          if (rect.bottom <= 0 || rect.top >= window.innerHeight) return false;
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0.05;
        }
      );
    }

    function scrolledPastAddToCart() {
      const atc = document.querySelector('.cnv-btn-atc');
      if (!atc) return window.scrollY > window.innerHeight * 0.6;
      return atc.getBoundingClientRect().bottom < 0;
    }

    function scheduleAutoOpen(delay) {
      clearTimeout(autoTimer);
      if (autoStopped()) return;
      autoTimer = setTimeout(tryAutoOpen, delay);
    }

    function tryAutoOpen() {
      if (autoStopped() || isOpen()) return;
      const busy = document.hidden || cartDrawerOpen() || otherModalOpen();

      if (DESKTOP_QUERY.matches) {
        if (autoOpens >= maxAutoOpens) return;
        if (busy) return scheduleAutoOpen(5000);
        autoOpens += 1;
        open(false);
        scheduleAutoClose();
        return;
      }

      if (!mobileAutoOpen || storageGet(MOBILE_OPENED_KEY)) return;
      if (busy || !scrolledPastAddToCart()) return scheduleAutoOpen(1500);
      storageSet(MOBILE_OPENED_KEY, '1');
      open(false);
    }

    // Desktop only: an untouched note tucks back into the pill, then comes back later
    function scheduleAutoClose() {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        if (hovering || dialog.contains(document.activeElement)) return scheduleAutoClose();
        close();
        scheduleAutoOpen(reopenEvery);
      }, visibleFor);
    }

    /* ── Events ── */
    pill.addEventListener('click', function () {
      clearTimeout(autoTimer);
      open(true);
    });

    closeBtn.addEventListener('click', function () {
      close();
      stopAutoOpen();
      pill.focus({ preventScroll: true });
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !isOpen()) return;
      close();
      stopAutoOpen();
      pill.focus({ preventScroll: true });
    });

    dialog.addEventListener('mouseenter', function () { hovering = true; });
    dialog.addEventListener('mouseleave', function () { hovering = false; });

    cards.forEach(function (card) {
      initCard(card, function () {
        close();
        stopAutoOpen();
      });
    });

    // Adding to the cart anywhere on the page opens the drawer: that also ends the automatic opening
    const drawer = document.querySelector('cart-drawer');
    if (drawer && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (drawer.classList.contains('active')) {
          if (isOpen()) close();
          stopAutoOpen();
        }
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    // Sit above the PDP sticky add-to-cart bar whenever it is on screen
    const stickyBar = document.querySelector('.cnv-sticky-bar');
    if (stickyBar && 'MutationObserver' in window) {
      const syncLift = function () {
        const shown = stickyBar.classList.contains('cnv-sticky-bar--visible')
          && window.getComputedStyle(stickyBar).display !== 'none';
        root.style.setProperty('--c-hannah-lift', shown ? (stickyBar.offsetHeight + 12) + 'px' : '0px');
      };
      new MutationObserver(syncLift).observe(stickyBar, { attributes: true, attributeFilter: ['class'] });
      syncLift();
    }

    setTimeout(function () {
      root.hidden = false;
      reveal(pill);
      if (autoOpenAfter > 0) scheduleAutoOpen(autoOpenAfter);
    }, pillDelay);
  }

  function boot() {
    document.querySelectorAll('[data-c-hannah]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
